import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, User } from "lucide-react";
import { KD9LogoHorizontal } from "@/components/KD9Logo";
import ThemeToggle from "@/components/ThemeToggle";
import LanguageToggle from "@/components/LanguageToggle";
import { useLanguage } from "@/context/LanguageContext";

const PublicNav = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();

  const NAV_LINKS = [
    { to: "/", label: t("nav.inicio") },
    { to: "/diagnosticos", label: t("nav.diagnosticos") },
    { to: "/academia", label: t("nav.academia") },
    { to: "/acompanamiento", label: t("nav.acompanamiento") },
    { to: "/sobre", label: t("nav.sobre") },
    { to: "/contacto", label: t("nav.contacto") },
    { to: "/descarga", label: "Descargar app" },
  ];

  return (
    <header
      className="sticky top-0 z-50 w-full backdrop-blur-xl"
      style={{
        background: "var(--kd9-overlay)",
        borderBottom: "1px solid var(--kd9-border)",
      }}
      data-testid="public-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-20 flex items-center justify-between gap-6">
        <Link to="/" className="flex-shrink-0" data-testid="nav-logo">
          <KD9LogoHorizontal size={36} />
        </Link>

        {/* Desktop links */}
        <nav className="hidden lg:flex items-center gap-7 flex-1 justify-center" translate="no">
          {NAV_LINKS.map((link, i) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              data-testid={`nav-link-${i}`}
              className={({ isActive }) =>
                `relative font-mont text-[12px] tracking-[0.18em] uppercase font-medium transition-colors duration-300 ${
                  isActive
                    ? "text-[color:var(--kd9-accent)]"
                    : "hover:text-[color:var(--kd9-accent)]"
                }`
              }
              style={({ isActive }) => ({
                color: isActive ? "var(--kd9-accent)" : "var(--kd9-text-soft)",
              })}
            >
              {({ isActive }) => (
                <>
                  {link.label}
                  {isActive && (
                    <span
                      className="absolute -bottom-2 left-0 right-0 h-[2px] rounded-full"
                      style={{
                        background:
                          "linear-gradient(90deg, transparent, var(--kd9-accent), transparent)",
                      }}
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <LanguageToggle />
          <ThemeToggle />
          <button
            onClick={() => navigate("/login")}
            className="kd9-btn-outline px-5 py-2.5 rounded-full text-[11px] flex items-center gap-2"
            data-testid="nav-login-btn"
          >
            <User size={14} />
            {t("nav.login")}
          </button>
        </div>

        {/* Mobile */}
        <div className="lg:hidden flex items-center gap-2">
          <LanguageToggle />
          <ThemeToggle />
          <button
            onClick={() => setOpen(!open)}
            className="w-10 h-10 rounded-full flex items-center justify-center border"
            style={{ borderColor: "var(--kd9-border)" }}
            data-testid="mobile-menu-toggle"
            aria-label="Menú"
          >
            {open ? (
              <X size={20} style={{ color: "var(--kd9-accent)" }} />
            ) : (
              <Menu size={20} style={{ color: "var(--kd9-accent)" }} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          className="lg:hidden border-t kd9-fade-up"
          style={{
            borderColor: "var(--kd9-border)",
            background: "var(--kd9-bg-2)",
          }}
          data-testid="mobile-menu"
        >
          <nav className="px-4 py-5 flex flex-col gap-1" translate="no">
            {NAV_LINKS.map((link, i) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${i}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-lg font-mont text-sm tracking-[0.12em] uppercase ${
                    isActive
                      ? "text-[color:var(--kd9-accent)] bg-[color:var(--kd9-accent-soft)]"
                      : ""
                  }`
                }
                style={({ isActive }) => ({
                  color: isActive ? "var(--kd9-accent)" : "var(--kd9-text-soft)",
                })}
              >
                {link.label}
              </NavLink>
            ))}
            <button
              onClick={() => {
                setOpen(false);
                navigate("/login");
              }}
              className="kd9-btn-primary mt-3 px-5 py-3 rounded-full text-[11px]"
              data-testid="mobile-login-btn"
            >
              {t("nav.login")}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default PublicNav;
